import reactUpdate, {Spec} from 'immutability-helper';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {EntryVersion} from 'notebook/ELNConstants';
import {
  FromServerTemplateVersion,
  TemplateVersion,
  ToServerTemplateVersion,
} from 'notebook/entry-templates/TemplateConstants';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import EntryVersionUtil from 'notebook/util/EntryVersionUtil';
import APIUtil from 'util/APIUtil';
import pick from 'util/pick';
import uniqueId from 'util/uniqueId';
import {StageConfiguration} from 'workflows/WorkflowConstants';

export interface FromServerWorkflowEntryTemplate extends StageConfiguration {
  latestVersionId: number;
  templateVersion: FromServerTemplateVersion;
}

export interface WorkflowEntryTemplate extends StageConfiguration {
  name: string;
  latestVersionId: number;
  templateVersion: TemplateVersion;
}

export function getServerNameField(entryTemplateId: string): 'entryTemplateName' | 'name' {
  return APIUtil.apiIdToType(entryTemplateId) === ModelTypeConstants.STAGE_CONFIGURATION
    ? 'entryTemplateName'
    : 'name';
}

function _prepareTPForStore(
  templateVersion: FromServerTemplateVersion,
  cidIdMap: CidIdMap
): {
  cid: string;
  entryVersion: EntryVersion;
  apiId: string;
} {
  const cid = uniqueId('setv');
  cidIdMap[cid] = templateVersion.id;
  return {
    cid,
    entryVersion: EntryVersionUtil.prepareForStore(templateVersion.entryVersion, cidIdMap),
    apiId: templateVersion.apiIdentifier,
  };
}

// Transform the server representation to the client representatiom
export function prepareForStore(
  entryTemplate: FromServerWorkflowEntryTemplate,
  cidIdMap: {[cid: string]: string}
): WorkflowEntryTemplate & {
  name: string;
  templateVersion: {cid: string; entryVersion: EntryVersion};
} {
  return {
    ...entryTemplate,
    name: entryTemplate[getServerNameField(entryTemplate.id) as 'entryTemplateName'],
    templateVersion: _prepareTPForStore(entryTemplate.templateVersion, cidIdMap),
  };
}

export function entryVersionUpdate(op: Spec<EntryVersion>): Spec<WorkflowEntryTemplate> {
  // Transforms the given `op` to operate on the entryVersion of a template.
  return {templateVersion: {entryVersion: op}};
}

export function sanitizeTemplate(
  entryTemplate: WorkflowEntryTemplate,
  oldTemplate: WorkflowEntryTemplate | null = null
): WorkflowEntryTemplate {
  const oldEntryVersion = oldTemplate ? oldTemplate.templateVersion.entryVersion : null;
  return reactUpdate<WorkflowEntryTemplate>(
    entryTemplate,
    entryVersionUpdate({
      $apply: (entryVersion: EntryVersion) =>
        EntryVersionUtil.sanitizeEntryVersion(entryVersion, oldEntryVersion, entryTemplate.id),
    })
  );
}

export function pickRevertableFields(entryTemplate: WorkflowEntryTemplate): {
  name: string;
  templateVersion: TemplateVersion;
} {
  return pick(entryTemplate, 'name', 'templateVersion');
}

function _prepareTVForSave(
  templateVersion: TemplateVersion,
  cidIdMap: CidIdMap,
  fileId: string
): ToServerTemplateVersion {
  return {
    id: cidIdMap[templateVersion.cid] as number,
    cid: templateVersion.cid,
    entryVersion: EntryVersionUtil.prepareForSave(templateVersion.entryVersion, cidIdMap, fileId),
  };
}

// Transform the client representation into the server representation
export function prepareForSave(
  entryTemplate: WorkflowEntryTemplate,
  cidIdMap: CidIdMap
):
  | {templateVersion: ToServerTemplateVersion; name: string}
  | {templateVersion: ToServerTemplateVersion; entryTemplateName: string} {
  return {
    templateVersion: _prepareTVForSave(entryTemplate.templateVersion, cidIdMap, entryTemplate.id),
    [getServerNameField(entryTemplate.id)]: entryTemplate.name,
  } as
    | {templateVersion: ToServerTemplateVersion; name: string}
    | {templateVersion: ToServerTemplateVersion; entryTemplateName: string};
}
